import type { ApiResponse, MentionBoostInsightsResponse } from '@fluenceur/types';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';

const MENTION_BOOST_INSIGHTS_QUERY_KEY_PREFIX = 'mentionBoostInsights';

export const mentionBoostInsightsQueryKey = (mentionId: string | null | undefined) => [
  MENTION_BOOST_INSIGHTS_QUERY_KEY_PREFIX,
  mentionId,
];

const fetchMentionBoostInsights = async (
  mentionId: string
): Promise<MentionBoostInsightsResponse> => {
  const response = await api.get<ApiResponse<MentionBoostInsightsResponse>>(
    `/dashboard/mentions/${mentionId}/boost-insights`
  );

  if (!response.data.data) {
    throw new Error(response.data.message || 'Mention boost insights are unavailable.');
  }

  return response.data.data;
};

interface UseMentionBoostInsightsQueryOptions {
  mentionId: string | null | undefined;
  enabled?: boolean;
}

export const useMentionBoostInsightsQuery = ({
  mentionId,
  enabled = true,
}: UseMentionBoostInsightsQueryOptions): UseQueryResult<MentionBoostInsightsResponse, Error> => {
  return useQuery<MentionBoostInsightsResponse, Error>({
    queryKey: mentionBoostInsightsQueryKey(mentionId),
    queryFn: () => {
      if (!mentionId) {
        return Promise.reject(new Error('Mention ID is required.'));
      }
      return fetchMentionBoostInsights(mentionId);
    },
    enabled: !!mentionId && enabled,
    // Paid figures move slowly; a 60s window keeps the pane fresh without
    // hammering the proxy each time the marketer flips back to it.
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
  });
};

export { fetchMentionBoostInsights };
